/**
 * Billing Mode Rules Resolution
 *
 * Resolves vehicle charging rules based on billing mode from vehicle verification.
 * This is the single source of truth for payment requirements and rate determination.
 *
 * BILLING MODES:
 * 1. PUBLIC_CARD - Standard public charger, card payment required
 * 2. DISCOUNTED_CARD - Discounted rate, card payment still required
 * 3. FLEET_INVOICE - Client-account session, no card capture (invoiced separately)
 * 4. FREE - Free charging, no payment required, zero rate
 * 5. CREDIT - Fully covered by a reimbursement code, no card capture
 */
import { billingModeLabel } from './billingMode';

/**
 * Resolve vehicle charging rules from verification response
 *
 * The station / server rate is authoritative (plan v3.1 §9.3 E, A6): the
 * verify endpoint no longer returns a per-vehicle rate override, and one
 * sent by an older API build is IGNORED here. The rate shown is the
 * session's pricing snapshot (which, after create, is the server tariff —
 * see utils/sessionAuthorization.js effectivePricingSnapshot), falling
 * back to station metadata; FREE is always zero.
 *
 * @param {Object} verifyResponse - Response from vehicle verification API
 * @param {string} verifyResponse.billingMode - PUBLIC_CARD | DISCOUNTED_CARD | FLEET_INVOICE | FREE | CREDIT
 * @param {Object} pricingSnapshot - Pricing of the session (server tariff once created)
 * @param {Object} stationMeta - Station metadata fallback
 *
 * @returns {{ billingMode: string, billingUnit: string, paymentRequired: boolean, rateApplied: number }}
 */
export function resolveVehicleChargingRules(verifyResponse, pricingSnapshot, stationMeta) {
  const billingMode = verifyResponse?.billingMode || 'PUBLIC_CARD'; // Default fallback

  const billingUnit = pricingSnapshot?.billing_unit || stationMeta?.billingUnit || 'kwh';
  const stationRate = billingUnit === 'minute'
    ? (Number(pricingSnapshot?.rate_per_minute) || Number(stationMeta?.ratePerMinute) || 0)
    : (Number(pricingSnapshot?.rate_per_kwh) || Number(stationMeta?.ratePerKwh) || 0);

  let paymentRequired = true;
  let rateApplied = stationRate;

  switch (billingMode) {
    case 'PUBLIC_CARD':
    case 'DISCOUNTED_CARD':
      // Card payment. DISCOUNTED_CARD pricing is decided server-side.
      paymentRequired = true;
      break;

    case 'FLEET_INVOICE':
      // Account billed — no card capture (invoiced separately).
      paymentRequired = false;
      break;

    case 'FREE':
      paymentRequired = false;
      rateApplied = 0; // Always zero for FREE
      break;

    default:
      // Unknown billing mode - fall back to PUBLIC_CARD behavior
      console.warn('[BillingRules] Unknown billing mode, defaulting to PUBLIC_CARD:', billingMode);
      paymentRequired = true;
      break;
  }

  return {
    billingMode,
    billingUnit,
    paymentRequired,
    rateApplied,
  };
}

/**
 * Check if payment step should be shown based on vehicle state.
 *
 * Fail-closed: only skip payment when the vehicle carries an
 * authoritative "no payment required" signal from the current
 * verification pass. Any ambiguity — missing vehicle, unverified
 * vehicle, undefined/null/malformed paymentRequired, or a non-strict
 * boolean `verified` — falls back to REQUIRING payment. This mirrors
 * the safety rule "do not infer 'no card required' from missing
 * payment fields, stale local state, or successful auth-budget entry
 * alone".
 *
 * Only skips when BOTH:
 *   - `vehicle.verified === true`      (strict-boolean, no coercion)
 *   - `vehicle.paymentRequired === false` (explicit false only)
 *
 * @param {Object} vehicle - Vehicle state from session
 * @returns {boolean} - True if payment step should be shown
 */
export function shouldShowPaymentStep(vehicle) {
  if (!vehicle) return true;
  if (vehicle.verified !== true) return true;
  return vehicle.paymentRequired !== false;
}

/**
 * Get display name for billing mode.
 *
 * Delegates to the canonical helper (src/utils/billingMode.js) so this
 * app's labels stay in lock-step with the admin UI and sw-ev-api.
 * Legacy label "Fleet Invoice" is replaced with "Account billed"; the
 * generic "Standard Charging" fallback is replaced with the canonical
 * "Not assigned" for unknown/missing values.
 *
 * @param {string} billingMode - Billing mode constant
 * @returns {string} - Human-readable name
 */
export function getBillingModeDisplayName(billingMode) {
  return billingModeLabel(billingMode);
}
